import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';

const statusColors = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error'
};

const statusLabels = {
  pending: 'Ожидает',
  approved: 'Активен',
  rejected: 'Отклонен'
};

function Users() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role_id: '',
    language: 'ru',
    is_active: true
  });
  const [approveRoleId, setApproveRoleId] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [tabValue]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const statusFilter = tabValue === 1 ? 'pending' : tabValue === 2 ? 'approved' : null;
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const response = await axios.get('/users/', { params });
      setUsers(response.data.items);
      setPendingCount(response.data.pending_count);
    } catch (err) {
      setError('Ошибка загрузки пользователей');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/roles/');
      setRoles(response.data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Create user
  const handleCreateOpen = () => {
    setFormData({
      email: '',
      full_name: '',
      password: '',
      role_id: roles.find(r => r.is_default)?.id || '',
      language: 'ru',
      is_active: true
    });
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      await axios.post('/users/', formData);
      setCreateDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания пользователя');
    }
  };

  // Edit user
  const handleEditOpen = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      password: '',
      role_id: user.role?.id || '',
      language: user.language || 'ru',
      is_active: user.is_active
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const data = { ...formData };
      if (!data.password) delete data.password;
      await axios.put(`/users/${selectedUser.id}`, data);
      setEditDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка обновления пользователя');
    }
  };

  // Approve user
  const handleApproveOpen = (user) => {
    setSelectedUser(user);
    setApproveRoleId(roles.find(r => r.is_default)?.id || '');
    setApproveDialogOpen(true);
  };

  const handleApproveSubmit = async () => {
    try {
      await axios.post(`/users/${selectedUser.id}/approve`, { role_id: approveRoleId || null });
      setApproveDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка одобрения пользователя');
    }
  };

  // Reject user
  const handleReject = async (user) => {
    if (!window.confirm(`Отклонить заявку от ${user.email}?`)) return;
    try {
      await axios.post(`/users/${user.id}/reject`, {});
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка отклонения заявки');
    }
  };

  // Delete user
  const handleDeleteOpen = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSubmit = async () => {
    try {
      await axios.delete(`/users/${selectedUser.id}`);
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления пользователя');
    }
  };

  const getRoleName = (user) => {
    if (user.is_superuser) return 'Суперадмин';
    return user.role?.name || 'Без роли';
  };

  const getRoleColor = (user) => {
    if (user.is_superuser) return '#9c27b0';
    return user.role?.color || '#757575';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Пользователи</Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            sx={{ mr: 1 }}
          >
            Обновить
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleCreateOpen}
          >
            Добавить
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Все" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Ожидают одобрения
                {pendingCount > 0 && (
                  <Chip
                    label={pendingCount}
                    color="warning"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            }
          />
          <Tab label="Активные" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Пользователь</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Роль</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Дата регистрации</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          src={user.avatar}
                          sx={{ mr: 2 }}
                        >
                          {user.full_name?.charAt(0) || user.email?.charAt(0)}
                        </Avatar>
                        <Typography>{user.full_name || '-'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleName(user)}
                        size="small"
                        sx={{
                          backgroundColor: getRoleColor(user),
                          color: 'white'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[user.status]}
                        color={statusColors[user.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString('ru-RU')}
                    </TableCell>
                    <TableCell align="right">
                      {user.status === 'pending' && (
                        <>
                          <Tooltip title="Одобрить">
                            <IconButton
                              color="success"
                              onClick={() => handleApproveOpen(user)}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Отклонить">
                            <IconButton
                              color="error"
                              onClick={() => handleReject(user)}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Редактировать">
                        <IconButton onClick={() => handleEditOpen(user)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {!user.is_superuser && (
                        <Tooltip title="Удалить">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteOpen(user)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <PendingIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">
                          Нет пользователей
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить пользователя</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Полное имя"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Пароль"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Роль</InputLabel>
            <Select
              value={formData.role_id}
              label="Роль"
              onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Язык</InputLabel>
            <Select
              value={formData.language}
              label="Язык"
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            >
              <MenuItem value="ru">Русский</MenuItem>
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="uk">Українська</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleCreateSubmit} variant="contained">Создать</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Редактировать пользователя</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Полное имя"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Новый пароль"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            helperText="Оставьте пустым, чтобы не менять"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Роль</InputLabel>
            <Select
              value={formData.role_id}
              label="Роль"
              onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
              disabled={selectedUser?.is_superuser}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Статус</InputLabel>
            <Select
              value={formData.is_active}
              label="Статус"
              onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
            >
              <MenuItem value={true}>Активен</MenuItem>
              <MenuItem value={false}>Заблокирован</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleEditSubmit} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Одобрить заявку</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Одобрить заявку от <strong>{selectedUser?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Имя: {selectedUser?.full_name || '-'}
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Назначить роль</InputLabel>
            <Select
              value={approveRoleId}
              label="Назначить роль"
              onChange={(e) => setApproveRoleId(e.target.value)}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name} {role.is_default && '(по умолчанию)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleApproveSubmit} variant="contained" color="success">
            Одобрить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Удалить пользователя</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить пользователя <strong>{selectedUser?.email}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteSubmit} variant="contained" color="error">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Users;
